import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Select, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, canViewAllRisks, hasPermission } from '../../utils';
import { Risk, RiskPriority, User } from '../../models';
import moment from 'moment';
import { RiskEditPage } from './risk-edit-page';
import { RiskDetailPage } from './risk-detail-page';
import { RiskDocument } from '../../models/risk-document';
import { UserContext } from '../contexts';
import { RiskStatus } from './risk-status';

export function RisksPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [risks, setRisks] = useState<Risk[]>();
  const [scope, setScope] = useState('user');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalRisk, setEditModalRisk] = useState<Risk>();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalRisk, setDetailModalRisk] = useState<Risk>();
  const [canEditAllRisks, setCanEditAllRisks] = useState(false);
  const [canEditAllSmats, setCanEditAllSmats] = useState(false);
  const [areaRisksAvailable, setAreaRisksAvialable] = useState(false);
  const [allRisksAvailable, setAllRisksAvialable] = useState(false);

  useEffect(() => {
    setAllRisksAvialable(canViewAllRisks(user));
    setAreaRisksAvialable(!!user?.areas?.length);
  }, [user]);

  useEffect(() => {
    setCanEditAllRisks(hasPermission(user, 'risks.edit'));
    setCanEditAllSmats(hasPermission(user, 'smats.edit'));
  }, [user]);

  const loadRisks = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`risks?scope=${scope}`)
      .then(response => {
        setRisks(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, [scope]);

  useEffect(() => {
    loadRisks();
  }, [loadRisks, scope]);

  const onRefreshClick = () => {
    loadRisks();
  };

  const onRiskDetailSaved = () => {
    loadRisks();
  };

  const onRiskEditSaved = () => {
    closeEditModal();
    loadRisks();
  };

  const openEditModal = () => {
    setEditModalRisk(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByRisk = (risk: Risk) => {
    setEditModalRisk(risk);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalRisk(undefined);
  };

  const openDetailModalByRisk = (risk: Risk) => {
    setDetailModalRisk(risk);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailModalRisk(undefined);
  };

  const deleteRisk = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`risks/${id}`)
      .then(() => {
        setLoading(false);
        loadRisks();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteRisk = (id: string) => {
    const onOk = () => deleteRisk(id);
    Modal.confirm({
      title: 'Risk bildirimi sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Risk bildirimini silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'details') {
      const risk = risks?.find(r => r.id === id);
      if (risk) {
        openDetailModalByRisk(risk);
      }
    } else if (e.key === 'edit') {
      const risk = risks?.find(m => m.id === id);
      if (risk) {
        openEditModalByRisk(risk);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteRisk(id);
    }
  };

  const tableActions = (id: string) => {
    let canEdit = canEditAllRisks;
    if (!canEdit) {
      const risk = risks?.find(r => r.id === id);
      if (risk?.smat) {
        if (canEditAllSmats || (risk?.smat?.user?.id === user?.id && (risk?.smat?.status === 1 || risk?.smat?.status === 3))) {
          canEdit = true;
        }
      }
    }
    return (
      <Menu onClick={(e: any) => onTableActionClick(e, id)}>
        <Menu.Item key="details">
          <Text>Detaylar</Text>
        </Menu.Item>
        {canEdit && (
          <>
            <Menu.Item key="edit">
              <Text>Düzenle</Text>
            </Menu.Item>
            <Menu.Item key="del">
              <Text type="danger">Sil</Text>
            </Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  const onScopeChange = (value: string) => {
    setScope(value);
  };

  const tableColumns: any[] = [
    {
      title: 'Risk no',
      dataIndex: 'riskNo',
      key: 'riskNo',
      render: (riskNo: string, risk: Risk) => (
        <Button type="link" onClick={() => openDetailModalByRisk(risk)}>
          {riskNo}
        </Button>
      ),
    },
    {
      title: 'SMAT',
      dataIndex: ['smat', 'smatNo'],
      key: 'smat',
    },
    {
      title: 'Alan kodu',
      dataIndex: ['area', 'code'],
      key: 'areaCode',
    },
    {
      title: 'Alan',
      dataIndex: ['area', 'name'],
      key: 'area',
    },
    {
      title: 'Kategori',
      dataIndex: ['riskCategory', 'name'],
      key: 'riskCategory',
    },
    {
      title: 'Görüşülen kişi sayısı',
      dataIndex: 'interviewCount',
      key: 'interviewCount',
    },
    {
      title: 'Güvensiz davranış mı?',
      dataIndex: 'unsafeAction',
      key: 'unsafeAction',
      render: (unsafeAction: boolean) => (unsafeAction ? 'Evet' : 'Hayır'),
    },
    {
      title: 'Güvensiz davranış isim',
      dataIndex: 'unsafeActionUser',
      key: 'unsafeActionUser',
      render: (unsafeActionUser: User) => unsafeActionUser?.fullNameId,
    },
    {
      title: 'Olasılık',
      dataIndex: 'beforeProbability',
      key: 'beforeProbability',
    },
    {
      title: 'Şiddet',
      dataIndex: 'beforeSeverity',
      key: 'beforeSeverity',
    },
    {
      title: 'Risk puanı',
      dataIndex: 'beforeRiskGrade',
      key: 'beforeRiskGrade',
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: RiskPriority) =>
        priority ? <span style={{ color: '#' + priority.color }}>{priority.name}</span> : <span>-</span>,
    },
    {
      title: 'Dokümanlar',
      dataIndex: 'documents',
      key: 'documents',
      render: (documents: RiskDocument[]) => documents?.length || '-',
    },
    {
      title: 'Oluşturan',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: User) => createdBy.fullNameId,
    },
    {
      title: 'Oluşturma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: 'Alan sorumlusu',
      dataIndex: ['area', 'responsibleUser'],
      key: 'responsibleUser',
      render: (responsibleUser: User) => (responsibleUser ? responsibleUser.fullNameId : '-'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, risk: Risk) => <RiskStatus risk={risk} />,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, risk: Risk) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(risk.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <ExclamationCircleOutlined /> Riskler
          </>
        }
        extra={[
          <>
            {(areaRisksAvailable || allRisksAvailable) && (
              <Select style={{ width: 150 }} value={scope} onChange={onScopeChange}>
                <Select.Option value="user">Gönderdiklerim</Select.Option>
                {areaRisksAvailable && <Select.Option value="area">Alanlarım</Select.Option>}
                {allRisksAvailable && <Select.Option value="all">Tümü</Select.Option>}
              </Select>
            )}
          </>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
            Risk bildir
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={risks} loading={loading} pagination={false} scroll={{ x: true }} />
      </Content>
      <Modal title="Risk bildirimi" visible={detailModalOpen} width={'50%'} footer={null} onCancel={closeDetailModal} destroyOnClose={true}>
        <RiskDetailPage riskId={detailModalRisk?.id} onSaved={onRiskDetailSaved} />
      </Modal>
      <Modal title="Risk bildirimi" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <RiskEditPage risk={editModalRisk} onSaved={onRiskEditSaved} />
      </Modal>
    </>
  );
}
